import { useEffect, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatState } from 'src/components/Chat/Chat.enum';
import type { tCurrentChat } from 'src/components/Chat/Chat.types';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { eChannelType, eChatEventStatus } from 'src/providers/SocketsProvider/Sockets.enums';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import {
  asWalletCurrency,
  formatComposerCostLabel,
  formatWalletUnitAmount,
} from '../Wallet.money';
import {
  type tComposerSealQuoteScope,
  type tFreeTierCounter,
  type tSendCostEstimate,
  type tWalletShortfall,
} from '../Wallet.types';
import { resolveHandleCountry } from 'src/functions/phone.functions';
import { countryLabel } from '../Wallet.country';
import { useWalletContext } from '../Wallet.context';
import { esEstampaFresca, freeTierRemaining } from '../Wallet.freeTier';
import { useWalletFlag } from './useWalletFlag';
import { useRegisterWapiComposerPresence } from './useWapiComposerPresence';
import { useSendCostEstimate } from './useSendCostEstimate';
import {
  isResolvedZeroQuote,
  isTemplateFreeAtSource,
  templateTypeToWalletCategory,
} from './useWalletSendGate';

/**
 * El sello del compositor de texto libre (RET-2232, docs/loop/RET-2232-estado.md).
 *
 * D-2: la lógica vive AQUÍ, en el slice del wallet — no en `TextAreaContainer`,
 * que es compartido por WhatsApp Web, Instagram, TikTok y Messenger. El
 * componente sólo pinta lo que este hook decide.
 *
 * D-3: un solo predicado (`isTemplateFreeAtSource`, el mismo que ya usan
 * Template.component.tsx, WabTemplatesVariablesAction y
 * WabTemplateScheduleAction), con `templateType: 'SERVICE'`. Este hook NO
 * deriva la fecha del corte por su cuenta: ni la lee, ni la repite, ni tiene
 * un valor por defecto propio — eso vive dentro del predicado único.
 *
 * ══════════════ Vuelta 4 (docs/loop/RET-2232-estado.md, "la causa raíz, no
 * los caminos") — D-25 a D-28 ══════════════
 *
 * Tres vueltas encontraron seis caminos donde el guardia dejaba pasar un
 * envío que no se podía pagar. No eran seis descuidos: `isBlocked` era una
 * conjunción de condiciones POSITIVAS (`seal?.kind === 'paid' &&
 * totalMicros !== null && balanceMicros !== null && !currencyMismatch &&
 * balanceMicros < totalMicros`), y cada incógnita que el front no sabía
 * volvía alguna en `false` — y `false` en esa conjunción significa *dejar
 * pasar*. El error de fondo: el guardia se derivaba del SELLO, dos preguntas
 * con default OPUESTO (el sello afirma sobre precio, ante la duda calla; el
 * guardia protege dinero, ante la duda no debería soltar).
 *
 *   - D-25 · `isBlocked` ya NO exige `seal?.kind === 'paid'`. Preguntar
 *     (cotizar) no es afirmar (pintar el sello) — ver `gateApplies`, abajo.
 *   - D-26 · `shouldQuote` ya NO exige `isChatActive`: un chat WAPI con
 *     `state` ausente monta el compositor (`MessageBar.content.tsx` usa la
 *     forma LAXA) y hoy no cotizaba nada. El SELLO sigue exigiendo
 *     `isChatActive` ESTRICTO (D-5) — sólo el guardia pregunta ahora.
 *   - D-27 · la insuficiencia la decide `balanceAfter` (el SERVIDOR, el mismo
 *     número que `useWalletSendGate.checkBalance` usa); el saldo LOCAL sólo
 *     LIBERA cuando SÍ cubre **y su lectura es más nueva que la cotización**
 *     (RET-2327, `isLocalBalanceFresher` abajo — antes de RET-2327 cubrir
 *     bastaba, sin mirar cuándo se leyó). Su ausencia (`balance === null`, un
 *     estado DURABLE: carga, error 500, workspace recién cambiado) ya no
 *     apaga el guardia indefinidamente.
 *   - D-28 · el ámbito (workspace, conversación) del veredicto sobrevive a un
 *     remontaje que no cambia de conversación, vía `quoteScopeRef` (un ref
 *     que vive en `Chat.hook.ts`, no en el estado de este hook ni de
 *     `TextAreaContainer`) — ver `tComposerSealQuoteScope` (Wallet.types.ts)
 *     y `readCachedQuote`, abajo.
 *   - RET-2327 · la cotización se recotiza dentro del MISMO chat cuando algo
 *     pudo mover el saldo: un envío saliente o un rechazo de ESTE chat suben
 *     `requoteToken` (`useSendCostEstimate`, C-3), vía `useSocketsContext()`.
 *   - RET-2079 · el guardia deja de bloquear en dos casos que no cuestan: lo
 *     que la FRANJA de Meta todavía cubre (lee el contador con un predicado
 *     propio, sin el margen del sello, y fail-closed) y una cotización
 *     resuelta en $0 exacto. Ver `freeTierStillCovers` e `isZeroTotalQuote`,
 *     junto a `isBlocked`, para el porqué.
 */
export type tComposerServiceSeal =
  | { kind: 'free-entry-point'; hoursLeft: number; minutesLeft: number }
  | { kind: 'free' }
  /**
   * RET-2283 · el CUARTO estado: «Gratis» porque el mensaje entra en la franja
   * de servicio que Meta no cobra este mes. Se afirma SÓLO con las dos
   * condiciones a la vez: `freeTierCounter.available === true` (un `false` es
   * el backend negándose a afirmar, con cualquiera de sus razones, y NO es
   * «cupo agotado») y `(cap − total) > 100` (banda ESTRICTA: exactamente 100
   * restantes NO afirma; `cap` o `total` en `null` tampoco — sin tope no hay
   * restantes). Sin cifra alguna: DS-20 prohíbe un número de restantes dentro
   * del chat, en el sello y en su hover. Pierde frente a los dos estados
   * gratis EN ORIGEN (punto de entrada vivo; ventana de 24 h antes del corte):
   * ahí no se cotiza ni se consulta cupo. Con el estado del chat desconocido
   * no se afirma (D-5 sigue). Es un estado del SELLO: no es lo que decide
   * `isBlocked`. El guardia lee el CONTADOR por su cuenta, con un predicado
   * propio y SIN el margen de 100 (RET-2079, D-2): ver `isBlocked` y
   * `freeTierStillCovers`, abajo. Las dos lecturas del contador no son la misma
   * pregunta —el sello afirma «Gratis», el guardia decide si se frena un
   * envío—, y por eso una franja con 50 restantes se pinta «Con costo» y
   * aun así no bloquea. La implicación va en un solo sentido: con la
   * invariante del contexto (contador no nulo ⟺ `status === 'ready'`), un
   * sello en `free-tier` (`restantes > 100`) implica una franja que cubre, y
   * el guardia tampoco bloquea. Contrato congelado por la vara de RET-2283.
   */
  | { kind: 'free-tier' }
  /**
   * BUG-1140 · el contador reporta la franja DENTRO de la banda
   * (`0 < restantes <= 100`): «Puede tener costo». Ahí el sistema no sabe si
   * todavía queda —el contador va atrasado—, así que el sello no afirma gratis
   * ni cobro, y el hover dice que el cupo puede ya haberse acabado, con la
   * tarifa. Igual que `free-tier`, es un estado del SELLO y no toca `isBlocked`.
   */
  | { kind: 'free-tier-low'; unitCostLabel: string | null }
  | { kind: 'paid'; unitCostLabel: string | null };

/**
 * El texto del hover, que LOS TRES estados llevan.
 *
 * El sello dice dos palabras y nada más: el precio unitario va al hover
 * porque siempre es el mismo y repetirlo entrena a mirar el número en vez de
 * la conversación (D-12). Eso deja al hover cargando lo que el sello no
 * puede decir, y el prototipo es explícito en cuál es esa carga: el sello ya
 * no nombra el saldo —se decidió «Con costo» sobre «Sale de tu saldo» porque
 * el par Gratis/Con costo contesta la misma pregunta— y «se compensa donde sí
 * se lee: el tooltip ABRE con "Se descuenta de tu saldo"».
 *
 * Por eso no basta con pasar la tarifa pelada, que es lo que hacía la primera
 * versión: sin esa oración el sello no nombra el saldo en ninguna parte.
 */

export type tUseComposerServiceSealResult = {
  seal: tComposerServiceSeal | null;
  /** El texto del hover del sello; '' cuando no hay sello. */
  sealTooltip: string;
  /** true ⇒ la barra sin saldo REEMPLAZA el compositor. */
  isBlocked: boolean;
  /** Total cotizado de ESTE mensaje, ya formateado; '' si no aplica. */
  costLabel: string;
  /** Faltante EXACTO para prellenar la recarga; null sin bloqueo. */
  shortfall: tWalletShortfall | null;
};

/**
 * Horas ENTERAS restantes hasta `finishesAtIso`, PISO (nunca redondeado hacia
 * arriba): 50 h 30 min ⇒ 50, nunca 51. Promete de menos, mismo criterio que
 * `WapiTimer.hook.ts` (`calculateRemainingTime`) aplicaba para el banner de la
 * ventana del anuncio que este ticket retira (D-14) — el cálculo se MUEVE aquí,
 * al slice del
 * wallet, en vez de reescribirse: mismo anidado de `Math.floor` (minutos
 * enteros, luego horas enteras), no una única división simplificada, para que
 * el resultado sea exactamente el mismo que el banner ya mostraba.
 */
/**
 * El estado de la ventana: si sigue ABIERTA, y cuánto le queda PARA PINTAR.
 *
 * D-41 · La corrección fue pasar de horas a minutos: la versión anterior
 * devolvía horas enteras y quien la consumía preguntaba `horas > 0` para
 * decidir si la ventana seguía viva, así que con 50 minutos por delante leía
 * «vencida» — los últimos 59 minutos de CADA ventana del anuncio se daban por
 * cerrados estando abiertos. El docblock de entonces ya decía la regla:
 * *redondear a cero y haber vencido son cosas distintas*.
 *
 * D-42 · Y aun así el error sobrevivió, sólo que más chico: con los minutos
 * al piso, los últimos 59 SEGUNDOS volvían a dar `0` y el consumidor volvía a
 * leerlos como vencidos. Bajó de una hora a un minuto, no desapareció. Lo
 * levantó CRisPR revisando el PR #3482.
 *
 * La causa no era el divisor, era que UN número contestaba DOS preguntas —
 * «¿sigue abierta?» y «¿qué número pinto?»— y sólo la segunda tolera
 * redondeo. Por eso esta función devuelve las dos por separado: `isOpen` sale
 * del INSTANTE crudo y no pasa por ninguna división, y los números son de
 * pintar. Mientras vivan juntos en un solo entero, cualquier unidad que se
 * elija deja su propio acantilado en el último tramo.
 */
type tFreeEntryPointWindow = {
  /** ¿Sigue abierta? Decidido contra el instante, nunca contra el redondeo. */
  isOpen: boolean;
  /** Minutos enteros (PISO) restantes, SÓLO para pintar. */
  minutesLeft: number;
  /** Horas enteras (PISO) restantes, SÓLO para pintar. */
  hoursLeft: number;
};

const CLOSED_WINDOW: tFreeEntryPointWindow = {
  isOpen: false,
  minutesLeft: 0,
  hoursLeft: 0,
};

function readFreeEntryPointWindow(
  finishesAtIso: string | undefined,
): tFreeEntryPointWindow {
  if (!finishesAtIso) return CLOSED_WINDOW;

  const diffMs = new Date(finishesAtIso).getTime() - Date.now();

  // Una fecha ilegible cierra la ventana: es el lado seguro del SELLO, que
  // ante la duda no promete «Gratis» (D-5). El guardia no se entera de esto
  // — lee la bandera CRUDA, ver `gateApplies`.
  if (!Number.isFinite(diffMs) || diffMs <= 0) return CLOSED_WINDOW;

  const minutesLeft = Math.floor(diffMs / (1000 * 60));

  return {
    isOpen: true,
    minutesLeft,
    hoursLeft: Math.floor(minutesLeft / 60),
  };
}

/**
 * El margen de la franja de Meta (RET-2233 → RET-2283): absorbe el retraso del
 * contador (`CO-12`: lo que publica es lo observado hasta el último corte, más
 * el desfase que mete la caché de 60 s del provider). Documentado en
 * `docs/loop/RET-2233-estado.md` ("La aritmética de la banda"): con 100 de
 * margen quedan ~92 para el retraso del backend al ritmo medido más alto.
 *
 * ESTRICTA y nunca pintada (`DS-20`): exactamente 100 restantes NO afirma.
 */
const FREE_TIER_BAND_MARGIN = 100;

/**
 * Las DOS condiciones para afirmar «Gratis» por la franja — las dos, o no se
 * afirma (RET-2283, docs/loop/RET-2233-estado.md):
 *
 *   1. `available === true`. Un `false` es el backend NEGÁNDOSE a afirmar
 *      —CINCO razones hoy, y ninguna es «cupo agotado»— y no es lo mismo que
 *      cero.
 *   2. `(cap - total) > FREE_TIER_BAND_MARGIN`. `cap` o `total` en `null`
 *      hereda la disciplina de ambos: sin tope (o sin consumido) no hay
 *      restantes que calcular, y eso NO es «cero restantes».
 *
 * `null` (el provider sin lectura vigente: cargando, fallida, cohorte apagada,
 * otro workspace) se lee igual que `available: false` — «no puedo afirmar».
 *
 * La aritmética (las dos condiciones y la disciplina de los nulos) vive en
 * `freeTierRemaining` (`Wallet.freeTier.ts`), porque el GUARDIA de saldo le
 * hace la misma pregunta al contador con otro umbral (RET-2079, D-2). Este
 * margen es del SELLO —afirmar «Gratis» exige holgura para el retraso del
 * contador— y NO se presta al guardia: ahí un margen de 100 frenaría un envío
 * que la franja todavía cubre.
 */
function isFreeTierBandOpen(counter: tFreeTierCounter | null): boolean {
  const remaining = freeTierRemaining(counter);

  return remaining !== null && remaining > FREE_TIER_BAND_MARGIN;
}

/**
 * BUG-1140 · ¿El contador reporta la franja DENTRO de la banda, todavía sin
 * agotarse? Es decir, `0 < restantes <= FREE_TIER_BAND_MARGIN`.
 *
 * Ahí el sello no puede afirmar «Gratis» (el retraso del contador puede haber
 * cruzado el tope), pero «Con costo» con «Se descuenta de tu saldo» se leía como
 * un cobro que todavía no pasa. Decisión de producto (Fanny, 27-oct): «Puede
 * tener costo», que es lo único cierto en toda la banda; sin cifra (DS-20). Una
 * primera versión decía «Quedan pocos gratis» y se descartó porque afirma que
 * todavía queda, justo donde no se sabe. Con la franja agotada (`<= 0`) o sin
 * restantes calculables (`null`) sigue «Con costo», como antes.
 *
 * SUPUESTO: el tope es mucho mayor que la banda (1 000 en `free_tier_caps` al
 * 27-oct-2026). Con un tope de 100 o menos los restantes nunca pasan de la
 * banda, y el sello diría «Puede tener costo» desde el primer mensaje del mes.
 * Es conservador —nunca promete «Gratis»—, pero si Meta bajara el tope a ese
 * orden habría que revisar el margen.
 */
function isFreeTierRunningLow(counter: tFreeTierCounter | null): boolean {
  const remaining = freeTierRemaining(counter);

  return remaining !== null && remaining > 0 && remaining <= FREE_TIER_BAND_MARGIN;
}

/**
 * D-28 (vuelta 4): lee la última cotización CONOCIDA para ESTE scope
 * (workspace, conversación, destinatario) desde el ref que vive por encima de
 * `TextAreaContainer` (`Chat.hook.ts`). Devuelve `null` sin ref (los arneses
 * de este archivo, y cualquier llamador fuera del árbol real del chat, no lo
 * pasan — el hook se comporta EXACTAMENTE como antes de D-28), sin scope
 * resoluble (falta `chatId` o `currentWorkspaceId`), o cuando lo guardado es
 * de OTRO scope.
 *
 * RET-2327 (C-2): la caché guarda `quotedAt` JUNTO con la cotización, y esta
 * lectura devuelve los dos — restituir una cotización remontada restituye
 * también la marca con la que se ADOPTÓ. Sin la marca el guardia no se abre
 * de más: `isLocalBalanceFresher` daría `false` y fallaría CERRADO. Lo que se
 * perdería es lo contrario: que un saldo leído DESPUÉS de esa cotización y
 * ANTES del remontaje (una recarga hecha con el compositor desmontado) suelte
 * la barra desde el primer render, sin esperar a la cotización nueva (D-8).
 */
function readCachedQuote(
  ref: MutableRefObject<tComposerSealQuoteScope> | undefined,
  scopeKey: string | null,
): { estimate: tSendCostEstimate; quotedAt: number } | null {
  if (!ref || !scopeKey) return null;
  const cached = ref.current;
  return cached && cached.scopeKey === scopeKey
    ? { estimate: cached.estimate, quotedAt: cached.quotedAt }
    : null;
}

export function useComposerServiceSeal(params: {
  selectedChat: tCurrentChat | null;
  recipientHandle: string | undefined;
  /**
   * El nombre del contacto, para el hover del punto de entrada: el prototipo
   * lo nombra («xystudio llegó por un anuncio de WhatsApp»). Ausente ⇒ la
   * variante impersonal, que dice lo mismo sin inventar a quién.
   */
  recipientName?: string;
  /**
   * D-28 (vuelta 4, OPCIONAL): el ámbito (workspace, conversación) que
   * sobrevive a un remontaje que NO cambia de conversación.
   * `TextAreaContainer` lo pasa desde `useChatContext()` (`Chat.hook.ts`, un
   * `useRef` que no vive en ese componente); sin él, este hook se comporta
   * exactamente igual que antes de D-28 — arranca en `null` en cada montaje.
   * Es el caso de la vara de este mismo archivo y de cualquier otro llamador
   * que no pase por el árbol real del chat.
   */
  quoteScopeRef?: MutableRefObject<tComposerSealQuoteScope>;
}): tUseComposerServiceSealResult {
  const { selectedChat, recipientHandle, quoteScopeRef } = params;
  const { t, i18n } = useTranslation('wallet');
  const { currentWorkspaceId } = useWorkspaceContext();
  const { isWalletEnabled, isLoadingWalletFlag } = useWalletFlag();
  const {
    balance,
    balanceReadStartedAt,
    freeTierCounter,
    freeTierCounterReadAt,
    freeTierCounterStatus,
  } = useWalletContext();
  const { chatInsertedEvent, chatUpdatedEvent } = useSocketsContext();

  const chatId = selectedChat?.id;
  const isWapiChat = selectedChat?.integration === eChannelType.WHATSAPP_API;

  /**
   * RET-2327 (C-3/C-4): sube con un envío SALIENTE o un rechazo de ESTE chat,
   * cualquiera de los dos — nunca un envío entrante, un `SENT`/`DELIVERY`/
   * `READ`, ni un evento de otro chat. No se filtra por código de error: el
   * front no conoce 3068 (saldo insuficiente en WBS) hasta RET-2257, y
   * cualquier rechazo es razón barata para volver a preguntar. Sólo se PASA a
   * `useSendCostEstimate`; nada aquí espera ni frena el envío.
   */
  const [requoteToken, setRequoteToken] = useState(0);

  chatInsertedEvent.useSubscription((event) => {
    if (event.chat_id === chatId && event.content?.from_me === true) {
      setRequoteToken((previous) => previous + 1);
    }
  });

  chatUpdatedEvent.useSubscription((event) => {
    if (event.chat_id === chatId && event.state === eChatEventStatus.ERROR) {
      setRequoteToken((previous) => previous + 1);
    }
  });

  // RET-2283: el ÚNICO avisador de "hay un chat WAPI abierto" para
  // `WalletProvider`, que vive por encima de `ChatProvider` y no puede
  // preguntarle a `selectedChat` por su cuenta — ver
  // `useWapiComposerPresence.ts`. Deliberadamente antes de cualquier filtro
  // de `isChatActive`: la lectura del contador es por WORKSPACE (`RE-13`), no
  // por si ESTE chat puede cotizar ahora mismo.
  useRegisterWapiComposerPresence(isWapiChat);
  // D-5: ESTRICTO. Un estado desconocido (INACTIVE, o el chat sin `state` en
  // absoluto) nunca promete gratuidad sobre dinero — aquí es donde esa lectura
  // vive, no en `MessageBar.content.tsx`, que sí puede darse el lujo de tratar
  // "desconocido" como activo sólo para decidir si el compositor se pinta.
  const isChatActive = selectedChat?.state === ChatState.ACTIVE;
  const hasFreeEntryPoint = Boolean(
    selectedChat?.freeEntryPointData?.hasFreeEntryPoint,
  );
  const fepFinishesAt = selectedChat?.freeEntryPointData?.FreeEntryPointFinishesAt;

  // D-18: `wholeMinutesUntil` lee `Date.now()` en render y nada más lo vuelve a
  // disparar. El banner que este ticket retira (`WapiTimer.hook.ts`) refrescaba
  // el número cada minuto con `setInterval(..., 60000)` — el cálculo se MUEVE
  // aquí, al slice del wallet, con el mismo período; esto sólo fuerza un
  // re-render, `wholeMinutesUntil` sigue leyendo `Date.now()` fresco en cada uno.
  const [, forceHoursRefresh] = useState(0);
  useEffect(() => {
    const intervalId = setInterval(() => {
      forceHoursRefresh((tick) => tick + 1);
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const walletUsable =
    Boolean(isWalletEnabled) && !isLoadingWalletFlag && Boolean(currentWorkspaceId);

  /**
   * Las horas que le quedan a la ventana del anuncio, y si sigue VIVA.
   *
   * El plazo NO se fija acá y no debe fijarse: llega resuelto del backend en
   * `free_entry_point_finishes_at`. Meta lo movió de 72 h a 168 h el
   * 28-sep-2026 sin anunciarlo y esta pieza no se enteró —ni tuvo que
   * enterarse— porque sólo resta contra esa fecha. Por eso ningún comentario
   * de este archivo vuelve a nombrar una duración.
   *
   * Se derivan una sola vez porque la bandera y el reloj pueden discrepar:
   * `hasFreeEntryPoint` se trae al abrir el chat y el reloj corre en local.
   */
  const fepWindow = hasFreeEntryPoint
    ? readFreeEntryPointWindow(fepFinishesAt)
    : CLOSED_WINDOW;
  // D-42 · La liveza sale de `isOpen` (el instante), NUNCA de que los minutos
  // pintables sean > 0: eso era el acantilado de los últimos 59 segundos.
  const hasLiveFreeEntryPoint = hasFreeEntryPoint && fepWindow.isOpen;
  const fepMinutesLeft = fepWindow.minutesLeft;
  const fepHoursLeft = fepWindow.hoursLeft;

  /*
    D-40 · DOS lecturas del MISMO predicado, con la bandera del punto de
    entrada en su versión VIVA y en su versión CRUDA. No son dos copias de la
    regla —es `isTemplateFreeAtSource` las dos veces, igual que RET-2234 lo
    llama con y sin `at`—: es la misma pregunta hecha desde dos sitios que
    fallan hacia lados OPUESTOS.

    Cuando el reloj local de la ventana llega a cero y la bandera sigue en true
    (se trajo al abrir el chat y nadie la refresca), las dos respuestas
    divergen, y cada consumidor tiene que quedarse con la suya:

    - el SELLO usa la VIVA y deja de prometer «Gratis», porque post-corte con
      la ventana de 24 h cerrada ese mensaje CUESTA y prometer de más sobre
      dinero es lo único que el ruling prohíbe;
    - el GUARDIA usa la CRUDA y sigue sin bloquear, porque si la bandera
      tuviera razón el envío es gratis y `DS-18` dice que ahí la falta de
      saldo nunca frena nada.

    Igualarlas rompe una de las dos: con la cruda en el sello se promete de
    más; con la viva en el guardia se bloquea un envío posiblemente gratis.
  */
  const isFreeAtSourceLive = isWapiChat
    ? isTemplateFreeAtSource({
        templateType: 'SERVICE',
        isChatActive,
        hasFreeEntryPoint: hasLiveFreeEntryPoint,
      })
    : false;

  const isFreeAtSourceRaw = isWapiChat
    ? isTemplateFreeAtSource({
        templateType: 'SERVICE',
        isChatActive,
        hasFreeEntryPoint,
      })
    : false;

  // D-6, guardrail duro: flag apagado o cargando ⇒ ni sello ni tráfico nuevo
  // al estimador. Canal distinto de WAPI: tampoco (espacio negativo 1).
  //
  // D-26 (vuelta 4): YA NO exige `isChatActive`. La premisa con la que se
  // congeló este archivo —"el compositor sólo se monta con el chat activo"—
  // es FALSA contra el árbol: `MessageBar.content.tsx` monta el texto libre
  // con la forma LAXA (`!selectedChat?.state || ... === ACTIVE`), así que un
  // chat WAPI con `state` AUSENTE también lo monta, y hasta esta vuelta eso
  // significaba sello callado, CERO cotizaciones y CERO guardia — el sexto
  // camino de las vueltas 1-3. El SELLO sigue exigiendo `isChatActive`
  // ESTRICTO más abajo (D-5: ante la duda, no afirma gratuidad); lo único que
  // cambia aquí es que el guardia SÍ pregunta.
  // Cotiza con la lectura VIVA: si la ventana se agotó, el sello va a decir
  // «Con costo» y necesita una tarifa que enseñar en el hover.
  const shouldQuote =
    isWapiChat && walletUsable && !isFreeAtSourceLive && Boolean(recipientHandle);

  const category = templateTypeToWalletCategory('SERVICE');

  // D-28 (vuelta 4): el ámbito (workspace, conversación, destinatario) del
  // veredicto — ver `tComposerSealQuoteScope` (Wallet.types.ts). `null` sin
  // `chatId`/`currentWorkspaceId` resolubles: sin scope no hay nada que
  // cachear ni que restituir.
  const scopeKey =
    currentWorkspaceId && chatId
      ? `${currentWorkspaceId}::${chatId}::${recipientHandle ?? ''}`
      : null;

  // `hasFailed` no se necesita aquí: D-7 dice que una cotización caída sigue
  // "Con costo" sin tarifa, y eso ya sale de `displayEstimate` quedándose en
  // `null` — no hay un tercer estado que representar.
  const { estimate } = useSendCostEstimate({
    category,
    recipients: recipientHandle ? [recipientHandle] : [],
    enabled: shouldQuote,
    // RET-2327 (C-3): un envío saliente o un rechazo de ESTE chat recotizan
    // la MISMA selección — ver `requoteToken` arriba.
    requoteToken,
  });

  /**
   * D-8 / espacio negativo 6: el veredicto tiene ámbito (workspace,
   * conversación). Al cambiar cualquiera de los dos la cotización cacheada
   * deja de aplicar — se suelta de inmediato, sin esperar a que llegue (o no
   * llegue) una respuesta nueva.
   *
   * D-28 (vuelta 4): el arranque YA NO es un `null` incondicional. Un
   * remontaje que NO cambia de scope (`ChatEnum.WABTEMPLATES_VARIABLES`
   * desmonta y vuelve a montar `TextAreaContainer` sin tocar `selectedChat`,
   * D-24) restituye la última cotización CONOCIDA vía `readCachedQuote` —
   * sin esto, el estado local nacía en `null`, el guardia recién calculado
   * decía "insuficiente" `false` (D-25/27 se lo permiten sin `seal`), y el
   * borrador quedaba enviable durante el debounce + RTT de la cotización
   * nueva, aunque ya se supiera que el saldo no alcanza.
   *
   * RET-2327 (C-4): `quotedAt` viaja PAREJO a `displayEstimate` — misma
   * restitución desde la caché, misma escritura al adoptar — porque es la
   * marca contra la que el saldo local se compara (`isLocalBalanceFresher`,
   * abajo).
   */
  const [displayEstimate, setDisplayEstimate] = useState<tSendCostEstimate | null>(
    () => readCachedQuote(quoteScopeRef, scopeKey)?.estimate ?? null,
  );
  const [quotedAt, setQuotedAt] = useState<number | null>(
    () => readCachedQuote(quoteScopeRef, scopeKey)?.quotedAt ?? null,
  );

  useEffect(() => {
    const cached = readCachedQuote(quoteScopeRef, scopeKey);
    setDisplayEstimate(cached?.estimate ?? null);
    setQuotedAt(cached?.quotedAt ?? null);
    // `quoteScopeRef` es un `useRef`: su identidad es estable y no participa
    // de esta comparación (regla ya reconocida por `exhaustive-deps`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId, chatId, recipientHandle, scopeKey]);

  useEffect(() => {
    if (!shouldQuote || !estimate) return;
    // RET-2327 (C-4): la marca de ADOPCIÓN, tomada AQUÍ — cuando el sello
    // acepta la respuesta, no cuando se pidió ni cuando el provider leyó el
    // saldo. Es el instante contra el que `isLocalBalanceFresher` compara.
    const adoptedAt = Date.now();
    setDisplayEstimate(estimate);
    setQuotedAt(adoptedAt);
    // D-28: la escritura al ref vive AQUÍ, no en un efecto aparte reactivo a
    // `scopeKey` — ese efecto correría también cuando sólo cambia el
    // destinatario, y `useSendCostEstimate` PRESERVA `previous.estimate`
    // mientras cotiza de nuevo (no lo limpia a `null`); un efecto disparado
    // por el cambio de scope escribiría entonces el estimado del chat VIEJO
    // bajo el `scopeKey` NUEVO. Aquí, en cambio, el efecto sólo corre cuando
    // `estimate` cambió a una respuesta de verdad, y en ese instante
    // `scopeKey` (mismo render) es del MISMO destinatario que pidió esa
    // respuesta — por construcción de `useSendCostEstimate` (que descarta lo
    // que llega tarde para un `recipientsKey` distinto).
    if (quoteScopeRef && scopeKey) {
      quoteScopeRef.current = { scopeKey, estimate, quotedAt: adoptedAt };
    }
  }, [estimate, shouldQuote]);

  // `unresolvedRecipients` no vacío (o, de forma equivalente,
  // `recipientsQuoted === 0`) es una cotización IMPOSIBLE, no un cero: D-7 /
  // mismo criterio que `useWalletSendGate.checkBalance`.
  const canQuote = displayEstimate !== null && displayEstimate.unresolvedRecipients.length === 0;
  // D-21: la moneda ABIERTA del estimador se estrecha con `asWalletCurrency`
  // (Wallet.money.ts), la exportada — no una copia privada. A diferencia de
  // sus otros dos consumidores (`useTemplateCosts`, `CampaignEstimatedCost`),
  // que caen al saldo (`|| balance?.currency`) cuando el estimador contesta
  // algo que el front no formatea, aquí NO hay ese respaldo: D-7 dice que sin
  // moneda resuelta no se pinta nada, y caer al saldo pintaría una tarifa en
  // una moneda que la cotización nunca dijo.
  const currency = canQuote ? asWalletCurrency(displayEstimate!.currency) || null : null;

  // D-12: SIEMPRE con `formatWalletUnitAmount` (decimales significativos),
  // nunca con el redondeo a centavos de `formatWalletAmount` — a dos
  // decimales una tarifa de US$0.0085 se pintaría "$0.00": el mismo "gratis"
  // inventado que este ticket existe para evitar.
  const unitCostLabel =
    canQuote && currency
      ? formatWalletUnitAmount({
          amountMicros: displayEstimate!.total.amountMicros,
          currency,
          locale: i18n.language,
        })
      : null;

  /**
   * RET-2319 · vuelta 3 · LA VEJEZ SE JUZGA AQUÍ, con el reloj de ESTE render, y
   * no se delega al `status` que llega del contexto.
   *
   * El provider también la juzga, y para el caso normal basta: un temporizador
   * degrada el `ready` al cumplirse la cota. Pero hay un commit que no alcanza.
   * `Date.now()` no puede ser dependencia de un `useMemo`, y cuando este
   * compositor se MONTA no cambia ninguna dep del memo del contexto — un hijo
   * que monta no re-renderiza a su padre, y la presencia se registra en un
   * `useEffect` PASIVO (`useWapiComposerPresence.ts`), así que su invalidación
   * llega después del commit. Si además el temporizador no llegó a disparar
   * —una máquina suspendida: el reloj de pared salta horas mientras el reloj
   * monótono que cuenta el `setTimeout` se pausa—, ese render sirve el valor
   * cacheado con el contador muerto y `status: 'ready'` dentro.
   *
   * Este hook sí se re-renderiza al montarse, así que es el único que ve el
   * reloj nuevo. Con la estampa en la mano calla, en vez de prometer franja
   * sobre un contador que pudo pasarse de los 1 000 hace rato.
   *
   * Y NO SÓLO AL MONTARSE, que es la parte fácil de perder: este hook también
   * se re-renderiza cada 60 s por su propio ticker (ver `useEffect` del reloj
   * del FEP, más abajo) y en cada tecla del compositor. Ese caso —montado, el
   * contexto sin cambiar, el reloj cruzando la cota— es el que el arreglo
   * cubre y el que `ret2319_useComposerServiceSeal.clockBetweenRenders.test.tsx`
   * (T-1, T-3) fija. Por eso `Date.now()` se llama EN EL RENDER y no se
   * memoiza ni se captura al montar: cachearlo reintroduce el defecto una capa
   * más arriba, con las varas del provider en verde. Medido: un
   * `useState(() => Date.now())` aquí pone rojas T-1, T-3 y S-1.
   *
   * Vencida se trata como `waiting`, no como `unknown`: es una espera —la
   * relectura del remontaje va en vuelo— y no un fallo. Y sin estampa tampoco
   * se afirma: si el contrato llegara roto, no hay con qué juzgar la vejez.
   */
  const freeTierReadingIsStale =
    freeTierCounterStatus === 'ready' &&
    (freeTierCounterReadAt === null ||
      !esEstampaFresca(freeTierCounterReadAt, Date.now()));

  let seal: tComposerServiceSeal | null = null;

  if (isWapiChat && walletUsable) {
    /*
      D-40 · Con la ventana del anuncio AGOTADA el sello NO puede seguir diciendo
      «Gratis».

      Antes, con `hasFreeEntryPoint` en true y 0 horas restantes, esto caía a
      `{ kind: 'free' }` — «Gratis» a secas. El razonamiento de D-19 era que
      «Gratis · 0 h» es un número sin sentido, y eso es cierto; la conclusión
      no lo era. Ese estado NO es gratis: es una ventana que ya cerró, y
      post-corte con la de 24 h cerrada el mensaje CUESTA. Prometía de más
      sobre dinero, que es justo lo que el ruling de la afirmación asimétrica
      prohíbe.

      Por qué pasa: `hasFreeEntryPoint` se trae al ABRIR el chat. Con el chat
      abierto un rato, el reloj local llega a cero mientras la bandera sigue
      en true — el front sabe que se acabó (tiene la fecha) pero la bandera
      no se entera hasta la siguiente lectura.

      Ahora, agotada la ventana, el sello CAE a las otras reglas: la de 24 h
      si el chat sigue activo, «Con costo» si no. El GUARDIA no cambia — sigue
      leyendo la bandera CRUDA, ver `gateApplies` más abajo.
    */
    if (hasLiveFreeEntryPoint) {
      // El del anuncio gana cuando las dos gratuidades aplican a la vez, y no
      // caduca con el CORTE de RET-2234 (su D-3) — sólo con su propio reloj.
      /*
        D-41 · Por debajo de una hora el sello cuenta MINUTOS, no cae a
        «Gratis» a secas ni pinta «Gratis · 0 h».

        Es la última hora de la ventana: el momento en que el número por fin
        sirve para decidir algo —contestar ya o dejarlo para mañana— y justo
        el momento en que redondear a horas lo borra. El banner que este
        ticket retira ya contaba minutos (`${hours} h ${minutes} min`); lo que
        se conserva es eso, no el formato.
      */
      seal = {
        kind: 'free-entry-point',
        hoursLeft: fepHoursLeft,
        minutesLeft: fepMinutesLeft,
      };
    } else if (isChatActive) {
      // RET-2283: gratis EN ORIGEN (la ventana de 24 h) gana sobre la franja
      // del cupo mensual — ahí no se cotiza ni se consulta cupo (precedencia
      // de docs/loop/RET-2233-estado.md, punto C). Sólo si NO es gratis en
      // origen se pregunta por la franja; `isFreeTierBandOpen` ya trata un
      // `freeTierCounter` nulo (sin lectura vigente) como "no puedo afirmar".
      //
      // RET-2319 · ese "no puedo afirmar" ya no cae siempre a «Con costo», y
      // la pregunta se le hace al DISCRIMINANTE del contexto, no a un booleano
      // al lado del contador. Son tres salidas y cada una tiene su respuesta:
      //
      //   `waiting`  la respuesta viene, o lo que hay es de otro workspace, o
      //              cruzó la cota de vejez de 120 s ⇒ el sello CALLA (`seal`
      //              sigue `null`). Afirmar precio aquí es afirmar sin datos,
      //              y es el defecto que este ticket cierra. La vejez se suma
      //              por `freeTierReadingIsStale`, que la vuelve a medir con el
      //              reloj de ESTE render: un `ready` que el provider no pudo
      //              degradar entra por aquí, no por la banda.
      //   `unknown`  la lectura cayó, o estamos arriba del provider ⇒ «Con
      //              costo». Callar PARA SIEMPRE esconde un costo real.
      //   `ready`    hay contador fresco ⇒ decide la banda, como antes.
      //
      // El orden importa: `waiting` se pregunta ANTES de la banda, porque con
      // la espera no hay contador que mirar. Y nótese que el silencio NO es
      // una afirmación de gratuidad — la propiedad que la vara de RET-2283
      // protege («nunca franja antes de que llegue») sigue intacta.
      seal = isFreeAtSourceLive
        ? { kind: 'free' }
        : freeTierCounterStatus === 'waiting' || freeTierReadingIsStale
          ? null
          : isFreeTierBandOpen(freeTierCounter)
            ? { kind: 'free-tier' }
            : isFreeTierRunningLow(freeTierCounter)
              ? { kind: 'free-tier-low', unitCostLabel }
              : { kind: 'paid', unitCostLabel };
    }
    // `isChatActive` false y sin FEP: nada que afirmar (D-5) — `seal` sigue
    // `null`, y por construcción `shouldQuote` ya era `false`.
  }

  const totalMicros =
    canQuote && currency ? BigInt(displayEstimate!.total.amountMicros) : null;
  const balanceMicros = balance ? BigInt(balance.amountMicros) : null;

  // D-17: restar saldo y costo sin comprobar que son la MISMA moneda es el
  // defecto. Hoy siempre coinciden porque wallet-service deriva las dos de la
  // misma suscripción, pero esa es una garantía AJENA (docblock de
  // `tWalletShortfall`, Wallet.types.ts) — no algo que este hook deba asumir.
  const currencyMismatch =
    currency !== null && balance !== null && balance.currency !== currency;

  // D-27 (vuelta 4): la insuficiencia la decide el SERVIDOR — `balanceAfter`,
  // el MISMO número que `useWalletSendGate.checkBalance` ya usa (G, la vara
  // de paridad) — nunca el saldo LOCAL. `balance === null` es un estado
  // DURABLE (la lectura cargando, un 500 del GET balance, un workspace recién
  // cambiado): antes de esta vuelta apagaba el guardia INDEFINIDAMENTE porque
  // `balanceMicros !== null` era una condición POSITIVA más en la conjunción
  // — el quinto camino de las vueltas 1-3.
  const balanceAfterMicros = canQuote
    ? BigInt(displayEstimate!.balanceAfter.amountMicros)
    : null;
  const serverSaysInsufficient =
    balanceAfterMicros !== null && balanceAfterMicros < 0n;

  /**
   * RET-2327 · «decide el número más NUEVO». El saldo local y la cotización
   * son la MISMA álgebra contra el servidor (ambos restan contra
   * `WorkspaceAvailable`); la única diferencia entre los dos es su EDAD, y el
   * bug era dejar que el más viejo mandara — un saldo leído al abrir el chat
   * seguía "cubriendo" horas después, sobre una cotización nueva e
   * insuficiente. `isLocalBalanceFresher` exige que la lectura del saldo
   * (C-1, instante de EMISIÓN) sea ESTRICTAMENTE posterior a la cotización
   * vigente (C-4, instante de ADOPCIÓN): con la misma marca no se puede
   * ordenar, y ante la duda el guardia no suelta.
   */
  const isLocalBalanceFresher =
    balanceReadStartedAt !== null && quotedAt !== null && balanceReadStartedAt > quotedAt;

  // El saldo LOCAL sólo LIBERA (D-8, D-27): existe para que una recarga suelte
  // la barra al instante, sin esperar a que se rehaga la cotización (cuyo
  // `balanceAfter` es del momento en que se cotizó). Por eso sólo puede
  // desmentir al servidor un saldo MÁS NUEVO que la cotización
  // (`isLocalBalanceFresher`, RET-2327): uno más viejo, por alto que sea, no
  // sabe nada de lo que se gastó después. Su ausencia, o la de su marca,
  // tampoco suelta. `!currencyMismatch` vive en `isBlocked`, no aquí, para que
  // un saldo en otra moneda tampoco pueda "cubrir" nada (D-17 sigue igual).
  const localBalanceCoversIt =
    isLocalBalanceFresher &&
    balanceMicros !== null &&
    totalMicros !== null &&
    balanceMicros >= totalMicros;

  // D-25 (vuelta 4): el guardia YA NO se deriva del sello
  // (`seal?.kind === 'paid'` desapareció de aquí). Son dos preguntas con
  // default OPUESTO: el sello AFIRMA sobre precio — ante la duda, calla
  // (D-5) —; el guardia PROTEGE dinero — ante la duda, no debería soltar.
  // Atar el segundo al primero hacía que toda la prudencia del sello (cada
  // incógnita que lo apagaba) se convirtiera en permisividad del guardia:
  // ahí vivían los seis caminos de las vueltas 1-3. `gateApplies` es la
  // condición positiva mínima que hace falta para que haya algo que
  // bloquear — WAPI, wallet usable, no gratis en origen —, y ninguna exige
  // que el SELLO haya podido afirmar nada. D-9 sigue intacto: gratis en
  // origen (`isFreeAtSource`) nunca bloquea, con FEP o sin él.
  //
  // D-40 · Y aquí la asimetría es DELIBERADA: usa `isFreeAtSourceRaw`, no la
  // lectura VIVA que el sello usa arriba. Con el reloj local agotado y la bandera todavía en true
  // los dos fallan hacia su lado seguro y son lados OPUESTOS: el sello deja
  // de prometer «Gratis» —no vaya a ser que cueste—, y el guardia sigue sin
  // bloquear —no vaya a ser que sea gratis y `DS-18` diga que ahí la falta de
  // saldo nunca frena un envío—. Igualarlos rompería una de las dos.
  const gateApplies = isWapiChat && walletUsable && !isFreeAtSourceRaw;

  /**
   * RET-2079 · LA FRANJA SUELTA AL GUARDIA, y por eso el guardia SÍ lee el
   * contador (antes no lo leía: sólo alimentaba el sello).
   *
   * Por qué: con la ventana de 24 h abierta, WBS —el guardia autoritativo—
   * autoriza el texto libre de servicio SIN mirar el saldo mientras la franja
   * de Meta cubra (`DS-19`), y no lo cobra. El estimador de wallet-service no
   * conoce la franja: cotiza el servicio a precio completo contra el saldo
   * disponible, así que con saldo en cero o negativo (legítimo, `DS-21`) el
   * `balanceAfter` sale negativo y el front frenaba un mensaje que WBS dejaría
   * pasar gratis. Es el caso que RET-2079 midió en producción: saldo de
   * −US$0.002, la franja casi entera por delante y el equipo contestando desde
   * el teléfono porque el compositor mostraba la barra «sin saldo» (`DS-20`: el
   * aviso no aparece mientras la franja cubra el mensaje).
   *
   * Equivocarse hacia el lado de soltar no cuesta dinero. En el eje de la
   * UNIDAD el front es más estricto: su contador suma por WORKSPACE y WBS
   * cuenta por número, así que `total_workspace >= count_número`. Pero no es
   * una garantía: la lectura puede tener hasta 120 s de retraso, y en el
   * cambio de mes el front pide el mes del NAVEGADOR (`Wallet.date.ts`)
   * mientras WBS corta en la zona de la WABA. En esos huecos el front puede
   * dejar pasar algo que WBS ya no cubre, y WBS lo rechaza (3068). El error
   * restrictivo, no el permisivo, es el que hoy se paga.
   *
   * PREDICADO PROPIO, SIN EL MARGEN DE 100 (D-2). No se usa `isFreeTierBandOpen`:
   * su margen es del SELLO, que AFIRMA «Gratis» y por eso exige holgura para el
   * retraso del contador. El guardia no afirma nada: decide si frena un envío
   * mientras QUEDE franja (`total < cap`, D-1). Con el margen, los últimos 100
   * mensajes de cada mes volverían a bloquear algo que WBS sí cubre. Lo que se
   * comparte con el sello es la aritmética y la disciplina de los nulos
   * (`freeTierRemaining`), no el umbral. El tope sale del dato (`cap`,
   * `UC-12`), nunca de una constante.
   *
   * FAIL-CLOSED (`DS-18`, D-3): si no se puede verificar que queda franja, se
   * bloquea como hasta hoy. Sólo suelta con las cuatro cosas a la vez:
   * `status === 'ready'`, lectura FRESCA, `available === true` y `cap` y
   * `total` presentes con `total < cap`. `waiting`, `unknown`, una lectura
   * vencida o sin estampa, un `available: false` y un `cap` o `total` ausentes
   * no sueltan nada.
   *
   * La vejez la juzga `freeTierReadingIsStale`, con el `Date.now()` de ESTE
   * render (RET-2319: un `useMemo` no puede vigilar el tiempo), la misma que
   * hace callar al sello: no hay una segunda cota que pueda discrepar.
   *
   * No toca `gateApplies` ni la asimetría D-40: la franja es una condición MÁS
   * para soltar, no una razón para que el guardia deje de aplicar.
   */
  const freeTierRemainingForGuard =
    freeTierCounterStatus === 'ready' && !freeTierReadingIsStale
      ? freeTierRemaining(freeTierCounter)
      : null;
  const freeTierStillCovers =
    freeTierRemainingForGuard !== null && freeTierRemainingForGuard > 0;

  /**
   * RET-2079 · un total de $0 EXACTO nunca bloquea (`DS-18`: la falta de saldo
   * no frena un mensaje que no cuesta, sea cual sea la razón). Con saldo
   * negativo, un total de 0 da `balanceAfter = saldo < 0` y la barra pintaba
   * «sin saldo» sobre un envío gratis.
   *
   * Sólo con cotización RESUELTA: la IMPOSIBLE (`unresolvedRecipients` no
   * vacío) no es un cero y sigue como hoy. La regla es cero exacto: 1 micro con
   * `balanceAfter < 0` SÍ bloquea. Mismo predicado que `checkBalance` de las
   * plantillas (`isResolvedZeroQuote`), para que los dos guardias no discrepen.
   */
  const isZeroTotalQuote =
    displayEstimate !== null && isResolvedZeroQuote(displayEstimate);

  const isBlocked =
    gateApplies &&
    !currencyMismatch &&
    serverSaysInsufficient &&
    !localBalanceCoversIt &&
    !freeTierStillCovers &&
    !isZeroTotalQuote;

  // RET-2356 · `DS-25`: el costo de la barra se lee a cuatro decimales hacia
  // arriba, igual que `unitCostLabel`. A centavos, US$0.0008 salía «$0.00».
  const costLabel =
    isBlocked && currency
      ? formatComposerCostLabel({
          amountMicros: displayEstimate!.total.amountMicros,
          currency,
          locale: i18n.language,
        })
      : '';

  // El faltante EXACTO: con saldo local en mano Y MÁS NUEVO que la cotización
  // (`isLocalBalanceFresher`, RET-2327), `total − saldo` (D-8, la cifra que el
  // CTA de recarga prellena); en cualquier otro caso (D-27, sin saldo, o saldo
  // VIEJO), el MISMO faltante que `checkBalance` ya calcula con el número del
  // servidor (`-balanceAfter`) — las dos cuentas son la MISMA álgebra cuando
  // el saldo local SÍ está (G, la vara de paridad de D-8). Sin esta condición
  // un saldo viejo y alto (p. ej. $10 contra un total de $0.03) restaría un
  // faltante NEGATIVO que el CTA de recarga prellenaría como basura.
  const shortfallMicros =
    isLocalBalanceFresher && balanceMicros !== null && totalMicros !== null
      ? totalMicros - balanceMicros
      : balanceAfterMicros !== null
        ? -balanceAfterMicros
        : null;

  const shortfall: tWalletShortfall | null =
    isBlocked && currency && shortfallMicros !== null
      ? { amountMicros: shortfallMicros.toString(), currency }
      : null;

  return { seal, isBlocked, costLabel, shortfall, sealTooltip: buildSealTooltip() };

  /**
   * El hover de cada estado, con la degradación explícita cuando falta un
   * dato: se quita la cláusula que no se puede sostener, nunca se inventa.
   * «Se descuenta de tu saldo» sobrevive siempre en el estado de pago —es la
   * única parte que no depende de una cotización— porque es justo la frase
   * que el sello ya no dice.
   */
  function buildSealTooltip(): string {
    if (!seal) return '';

    if (seal.kind === 'free') return t('composer.sealTooltip.free');

    if (seal.kind === 'free-entry-point') {
      const restante =
        seal.hoursLeft > 0
          ? t('composer.sealTooltip.remainingHours', { hours: seal.hoursLeft })
          : t('composer.sealTooltip.remainingMinutes', {
              minutes: seal.minutesLeft,
            });

      return params.recipientName
        ? t('composer.sealTooltip.freeEntryPoint', {
            name: params.recipientName,
            remaining: restante,
          })
        : t('composer.sealTooltip.freeEntryPointNoName', {
            remaining: restante,
          });
    }

    // RET-2283: el hover de la franja. SIN cifra alguna (DS-20: ni restantes,
    // ni tope, ni consumido) — llave congelada por la vara, verificada de
    // disco (`ret2283_composerSeal.freeTier.i18n.test.ts`) para que ningún
    // número se le pueda colar después.
    if (seal.kind === 'free-tier') return t('composer.sealTooltip.freeTier');

    // A partir de aquí `seal.kind` sólo puede ser 'free-tier-low' o 'paid'. Los
    // dos degradan igual (BUG-1140): sin tarifa no hay nada que cuantificar,
    // pero el saldo se nombra igual.
    const isLow = seal.kind === 'free-tier-low';

    if (!seal.unitCostLabel) {
      return isLow
        ? t('composer.sealTooltip.freeTierLowNoCost')
        : t('composer.sealTooltip.paidNoCost');
    }

    const country = resolveHandleCountry(params.recipientHandle);
    const countryName = country
      ? countryLabel(country.iso2, i18n.language)
      : null;

    if (!countryName) {
      return isLow
        ? t('composer.sealTooltip.freeTierLowNoCountry', { cost: seal.unitCostLabel })
        : t('composer.sealTooltip.paidNoCountry', { cost: seal.unitCostLabel });
    }

    const tariff = { cost: seal.unitCostLabel, country: countryName };

    return isLow
      ? t('composer.sealTooltip.freeTierLow', tariff)
      : t('composer.sealTooltip.paid', tariff);
  }
}
